import { useState, useCallback } from 'react';
import { onboardingAPI } from '../services/apiService';
import { SelectedLocation } from '../types'; 

export interface OnboardingData {
  // Step 1: Housing
  location: SelectedLocation[];
  housingSituation: 'looking' | 'have-apartment' | 'flexible' | '';
  budget: string;
  moveInDate: string;
  age: number | '';
  
  // Step 2: All fields flat (no nesting)
  occupation: string;
  languages: string[];
  cleanliness: string;
  socialLevel: string;
  workSchedule: string;
  hasPets: string[];
  okWithPets: string[];
  bio: string;
}

const initialData: OnboardingData = {
    // Step 1: Housing
    location: [],
    housingSituation: '',
    budget: '',
    moveInDate: '',
    age: '',
  
    // Step 2: All fields flat
    occupation: '',
    languages: [],
    cleanliness: '',
    socialLevel: '',
    workSchedule: '',
    hasPets: [],
    okWithPets: [],
    bio: ''
};

export const useOnboarding = () => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const nextStep = () => {
    console.log('nextStep called, current step:', step);
    setStep(prev => {
      const newStep = Math.min(prev + 1, 3);
      console.log('Setting step from', prev, 'to', newStep);
      return newStep;
    });
  };
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const saveStep = async (token: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      // If we're on step 3 (review), we need to complete onboarding
      if (step === 3) {
        // First save any remaining data, then complete
        const response = await onboardingAPI.completeOnboarding(token);
        
        if (response.success) {
          return true;
        }
        
        setError(response.message || 'Failed to complete onboarding');
        return false;
      }

      // For steps 1 and 2, save the step data
      const stepData = {
        step,
        data: {
            // Step 1 data
            location: data.location,
            housingSituation: data.housingSituation,
            budget: data.budget,
            moveInDate: data.moveInDate,
            age: data.age === '' ? undefined : data.age,

            // Step 2 data (all flat)
            occupation: data.occupation,
            languages: data.languages,
            cleanliness: data.cleanliness,
            socialLevel: data.socialLevel,
            workSchedule: data.workSchedule,
            hasPets: data.hasPets,
            okWithPets: data.okWithPets,
            bio: data.bio
        }
      };

      const response = await onboardingAPI.updateStep(stepData, token);
      
      console.log('Onboarding API response:', response);
      
      if (response.success) {
        return true;
      }
            
      console.error('API returned success: false', response);
      setError(response.message || 'Failed to save step');
      return false;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setData(initialData);
    setError(null);
  };

  return {
    step,
    data,
    loading,
    error,
    updateData,
    nextStep,
    prevStep,
    saveStep,
    reset,
    totalSteps: 3
  };
};